import {
  extractEmailDomain,
  runEmailDomainHealthChecks,
  type EmailDomainHealthProgress,
  type EmailDomainHealthResult,
} from '@/lib/emailDomainHealth';

const MAX_CONCURRENT = 3;

type CacheKey = string;

type QueueItem = {
  key: CacheKey;
  email: string | null;
};

const resultCache = new Map<CacheKey, EmailDomainHealthResult>();
const progressCache = new Map<CacheKey, EmailDomainHealthProgress>();
const queuedKeys = new Set<CacheKey>();
const runningKeys = new Set<CacheKey>();
const queue: QueueItem[] = [];
const emailByKey = new Map<CacheKey, string | null>();
const forceAfterComplete = new Set<CacheKey>();

let runningCount = 0;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => listener());
}

export function subscribeEmailDomainHealthCache(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function cacheKeyForEmail(email: string | null | undefined): CacheKey | null {
  if (!email?.trim()) return '__no_email__';
  const domain = extractEmailDomain(email);
  if (domain) return domain;
  return `__invalid__:${email.trim().toLowerCase()}`;
}

function moveQueueItemToFront(key: CacheKey) {
  const index = queue.findIndex((item) => item.key === key);
  if (index <= 0) return;
  const [item] = queue.splice(index, 1);
  queue.unshift(item);
}

function pumpQueue() {
  while (runningCount < MAX_CONCURRENT && queue.length > 0) {
    const item = queue.shift()!;
    queuedKeys.delete(item.key);

    if (resultCache.has(item.key)) {
      continue;
    }

    runningKeys.add(item.key);
    runningCount += 1;

    void runEmailDomainHealthChecks(item.email, (snapshot) => {
      progressCache.set(item.key, snapshot);
      notify();
    })
      .then((result) => {
        resultCache.set(item.key, result);
        progressCache.delete(item.key);
      })
      .catch(() => {
        progressCache.delete(item.key);
        resultCache.set(item.key, {
          domain: extractEmailDomain(item.email),
          checks: [
            {
              id: 'error',
              label: 'Check failed',
              status: 'warn',
              detail: 'Unexpected error',
            },
          ],
          hasFailure: false,
          hasWarning: true,
        });
      })
      .finally(() => {
        runningKeys.delete(item.key);
        runningCount -= 1;

        if (forceAfterComplete.has(item.key)) {
          forceAfterComplete.delete(item.key);
          resultCache.delete(item.key);
          queuedKeys.add(item.key);
          queue.unshift({
            key: item.key,
            email: emailByKey.get(item.key) ?? item.email,
          });
        }

        notify();
        pumpQueue();
      });
  }
}

export type RequestEmailDomainHealthOptions = {
  /** Re-run even if a result is cached (Recheck). */
  force?: boolean;
  /** Prefer this domain over queued prefetch work. */
  priority?: boolean;
};

export function requestEmailDomainHealth(
  email: string | null | undefined,
  options?: RequestEmailDomainHealthOptions
): void {
  const key = cacheKeyForEmail(email);
  if (!key) return;

  emailByKey.set(key, email ?? null);

  if (options?.force) {
    resultCache.delete(key);
    progressCache.delete(key);
    queuedKeys.delete(key);
    const qIndex = queue.findIndex((item) => item.key === key);
    if (qIndex >= 0) queue.splice(qIndex, 1);
    if (runningKeys.has(key)) {
      forceAfterComplete.add(key);
      notify();
      return;
    }
  } else if (resultCache.has(key)) {
    return;
  }

  if (runningKeys.has(key)) {
    return;
  }

  if (queuedKeys.has(key)) {
    if (options?.priority) moveQueueItemToFront(key);
    return;
  }

  queuedKeys.add(key);
  const item: QueueItem = { key, email: email ?? null };
  if (options?.priority) queue.unshift(item);
  else queue.push(item);

  notify();
  pumpQueue();
}

export function prefetchEmailDomainHealth(
  emails: Iterable<string | null | undefined>
): void {
  const seen = new Set<CacheKey>();
  for (const email of emails) {
    const key = cacheKeyForEmail(email);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    requestEmailDomainHealth(email);
  }
}

export type EmailDomainHealthCacheState = {
  domain: string | null;
  result: EmailDomainHealthResult | null;
  checking: boolean;
};

export function getEmailDomainHealthCacheState(
  email: string | null | undefined
): EmailDomainHealthCacheState {
  const domain = extractEmailDomain(email);
  const key = cacheKeyForEmail(email);

  if (!key) {
    return { domain: null, result: null, checking: false };
  }

  const cached = resultCache.get(key);
  if (cached) {
    return { domain: cached.domain ?? domain, result: cached, checking: false };
  }

  const inFlight = progressCache.get(key);
  const checking = queuedKeys.has(key) || runningKeys.has(key);

  if (inFlight) {
    return { domain: inFlight.domain ?? domain, result: inFlight, checking: true };
  }

  if (checking) {
    return {
      domain,
      result: {
        domain,
        checks: [],
        hasFailure: false,
        hasWarning: false,
      },
      checking: true,
    };
  }

  return { domain, result: null, checking: false };
}

export type EmailDomainRowIndicator = 'none' | 'loading' | 'ok' | 'bad';

export function getEmailDomainRowIndicator(
  email: string | null | undefined
): EmailDomainRowIndicator {
  if (!email?.trim()) return 'none';

  const key = cacheKeyForEmail(email);
  if (!key) return 'none';

  if (queuedKeys.has(key) || runningKeys.has(key)) {
    return 'loading';
  }

  const cached = resultCache.get(key);
  if (!cached) return 'none';

  return cached.hasFailure ? 'bad' : 'ok';
}

/** Domains waiting or running (for debugging / optional UI). */
export function emailDomainHealthCacheStats(): {
  cached: number;
  queued: number;
  running: number;
} {
  return {
    cached: resultCache.size,
    queued: queuedKeys.size,
    running: runningKeys.size,
  };
}
